/**
 * Grafico del rendimento di un portafoglio (F-182), aperto dal riquadro
 * «P&L non realizzata» del dettaglio.
 *
 * La domanda a cui risponde non è «quanto ho fatto oggi» ma «a fine di ogni
 * giorno ero in guadagno o in perdita, e di quanto». Tre curve, tutte cumulate:
 * non realizzata (la vista d'arrivo), realizzata, e combinata — lo stato
 * complessivo. Sotto il cursore una nuvoletta dice il giorno e le operazioni
 * che l'hanno mosso; un clic fissa il giorno nel riquadro accanto, dove le
 * vendite mostrano quanto hanno spostato dal non realizzato al realizzato.
 *
 * Periodo e curva stanno nell'indirizzo: un link a «la non realizzata degli
 * ultimi 90 giorni» riapre esattamente quella.
 */

import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDate, formatQuantity, signOf } from '@/lib/format';
import { Alert, Card, EmptyState, Spinner, Switch } from '@/ui';
import { usePortfolio, usePortfolioPerformance, type PerformanceOperation, type PortfolioPerformance } from './api';
import { overlayToken } from './performance-colors';
import { PerformanceChart, type PerformanceChartMode, type PerformanceOverlay } from './PerformanceChart';
import {
  METRICS,
  METRIC_LABELS,
  buildBuckets,
  defaultSelection,
  isMetric,
  summarize,
  type CandleWidth,
  type PerformanceBucket,
  type PerformanceMetric,
} from './performance-series';
import { Segmented } from './Segmented';
import { TxTypeBadge } from './TxTypeBadge';
import './portfolios.css';

const PERIODS: readonly { days: number | null; label: string; name: string }[] = [
  { days: 7, label: '7 G', name: 'Ultimi 7 giorni' },
  { days: 30, label: '30 G', name: 'Ultimi 30 giorni' },
  { days: 90, label: '90 G', name: 'Ultimi 90 giorni' },
  { days: 365, label: '1 A', name: 'Ultimo anno' },
  { days: null, label: 'Tutto', name: 'Tutto lo storico' },
];

const DEFAULT_DAYS = 30;

function parseDays(value: string | null): number | null {
  if (value === 'all') return null;
  const match = PERIODS.find((period) => period.days !== null && String(period.days) === value);
  return match ? match.days : DEFAULT_DAYS;
}

function toneClass(value: string | number | null): string {
  const sign = signOf(value);
  return sign === 'positive' ? 'text-positive' : sign === 'negative' ? 'text-negative' : '';
}

function statusOf(value: string | number | null): string {
  const sign = signOf(value);
  return sign === 'positive' ? 'in guadagno' : sign === 'negative' ? 'in perdita' : 'in pari';
}

/** Importo con il segno sempre esplicito: qui il segno è lo stato. */
function signed(value: string | number | null, currency: string): string {
  if (value === null) return '—';
  const text = formatCurrency(value, currency);
  return signOf(value) === 'positive' ? `+${text}` : text;
}

function formatDayLong(date: string): string {
  return new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

function formatDayShort(date: string): string {
  return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

function bucketTitle(bucket: PerformanceBucket): string {
  return bucket.from === bucket.to
    ? formatDayLong(bucket.from)
    : `Settimana dal ${formatDayShort(bucket.from)} al ${formatDayShort(bucket.to)}`;
}

/** I tre valori a fine secchio: l'ultimo giorno, e per la non realizzata l'ultimo con una fotografia. */
function bucketValues(bucket: PerformanceBucket) {
  const last = bucket.days.at(-1)!;
  const lastValued = [...bucket.days].reverse().find((day) => day.unrealized !== null) ?? null;
  return {
    unrealized: lastValued?.unrealized ?? null,
    realized: last.realized,
    combined: lastValued?.combined ?? null,
  };
}

function OperationLine({
  operation,
  portfolioId,
  showDay,
  linked,
}: {
  operation: PerformanceOperation;
  portfolioId: string;
  showDay: boolean;
  linked: boolean;
}) {
  const name = operation.instrument?.name ?? '—';
  const time = new Intl.DateTimeFormat('it-IT', {
    ...(showDay ? { weekday: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(operation.txDate));

  return (
    <li className="perf-op">
      <div className="perf-op__head">
        <TxTypeBadge txType={operation.txType} />
        {linked && operation.instrument ? (
          <Link className="perf-op__name" to={`/portfolios/${portfolioId}/instruments/${operation.instrument.id}`}>
            {name}
          </Link>
        ) : (
          <span className="perf-op__name">{name}</span>
        )}
        <span className="perf-op__time">{time}</span>
      </div>
      <span className="perf-op__detail numeric">
        {operation.quantity && operation.price
          ? `${formatQuantity(operation.quantity.replace('-', ''))} × ${formatCurrency(operation.price, operation.currency)} = `
          : ''}
        {signed(operation.amount, operation.currency)}
      </span>
      {operation.sale &&
        (operation.sale.costUnknown ? (
          <span className="perf-op__realized text-muted">
            Costo di carico ignoto: il realizzato coinciderebbe con l’incasso, cioè sarebbe sovrastimato.
          </span>
        ) : (
          <span className={`perf-op__realized ${toneClass(operation.sale.realized)}`}>
            Realizzato {signed(operation.sale.realized, operation.currency)}
            <span className="perf-op__versus">
              {' '}
              · carico medio {formatCurrency(operation.sale.averageCost, operation.currency)}
            </span>
          </span>
        ))}
    </li>
  );
}

/** Il contenuto comune a nuvoletta e riquadro: valori del giorno e operazioni. */
function BucketValues({
  bucket,
  metric,
  currency,
}: {
  bucket: PerformanceBucket;
  metric: PerformanceMetric;
  currency: string;
}) {
  const values = bucketValues(bucket);
  return (
    <dl className="perf-values">
      {METRICS.map((key) => (
        <div key={key} className={key === metric ? 'perf-values__row perf-values__row--current' : 'perf-values__row'}>
          <dt>{METRIC_LABELS[key]}</dt>
          <dd className={`numeric ${toneClass(values[key])}`}>
            {values[key] === null ? <span className="text-muted">nessuna fotografia</span> : signed(values[key], currency)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function changeLine(bucket: PerformanceBucket, metric: PerformanceMetric, currency: string): string | null {
  if (bucket.close === null || bucket.open === null) return null;
  const change = bucket.close - bucket.open;
  const scope = bucket.from === bucket.to ? 'Rispetto al giorno prima' : 'Nella settimana';
  return `${scope}: ${signed(change, currency)} (${METRIC_LABELS[metric].toLowerCase()})`;
}

function Tooltip({ bucket, metric, currency }: { bucket: PerformanceBucket; metric: PerformanceMetric; currency: string }) {
  const change = changeLine(bucket, metric, currency);
  const shown = bucket.operations.slice(0, 4);
  return (
    <div className="perf-tip">
      <p className="perf-tip__date">{bucketTitle(bucket)}</p>
      <BucketValues bucket={bucket} metric={metric} currency={currency} />
      {change && <p className="perf-tip__change">{change}</p>}
      {shown.length > 0 ? (
        <ul className="perf-ops perf-ops--compact">
          {shown.map((operation) => (
            <OperationLine
              key={operation.id}
              operation={operation}
              portfolioId=""
              showDay={bucket.from !== bucket.to}
              linked={false}
            />
          ))}
          {bucket.operations.length > shown.length && (
            <li className="perf-tip__more">e altre {bucket.operations.length - shown.length} operazioni</li>
          )}
        </ul>
      ) : (
        <p className="perf-tip__quiet">Nessuna operazione: si è mosso solo il mercato.</p>
      )}
      <p className="perf-tip__hint">Clic per fissare il giorno</p>
    </div>
  );
}

function SelectedBucket({
  bucket,
  metric,
  currency,
  portfolioId,
}: {
  bucket: PerformanceBucket;
  metric: PerformanceMetric;
  currency: string;
  portfolioId: string;
}) {
  const values = bucketValues(bucket);
  const sales = bucket.operations.filter((operation) => operation.sale !== null && !operation.sale.costUnknown);
  const realizedHere = sales.reduce((total, operation) => total + Number(operation.sale!.realized), 0);
  const change = changeLine(bucket, metric, currency);

  return (
    <aside className="perf-day" aria-label="Giorno selezionato">
      <div>
        <p className="perf-day__eyebrow">{bucket.from === bucket.to ? 'Giorno selezionato' : 'Settimana selezionata'}</p>
        <h2 className="perf-day__title">{bucketTitle(bucket)}</h2>
      </div>
      <BucketValues bucket={bucket} metric={metric} currency={currency} />
      {change && <p className="perf-day__change">{change}</p>}
      <p className="perf-day__explain">
        {sales.length > 0
          ? `Le vendite hanno spostato ${signed(realizedHere, currency)} dal non realizzato al realizzato. Una vendita da sola non cambia la combinata: è il mercato a muoverla.`
          : values.combined !== null
            ? `A fine giornata, sommando realizzato e non realizzato, il portafoglio era ${statusOf(values.combined)}${signOf(values.combined) === 'zero' ? '' : ` di ${formatCurrency(Math.abs(Number(values.combined)), currency)}`}.`
            : 'Nessuna fotografia del portafoglio in questo giorno: la non realizzata non è nota, la realizzata sì.'}
      </p>
      <div className="perf-day__ops">
        <h3 className="perf-day__eyebrow">Operazioni ({bucket.operations.length})</h3>
        {bucket.operations.length === 0 ? (
          <p className="text-muted">Nessun acquisto né vendita.</p>
        ) : (
          <ul className="perf-ops">
            {bucket.operations.map((operation) => (
              <OperationLine
                key={operation.id}
                operation={operation}
                portfolioId={portfolioId}
                showDay={bucket.from !== bucket.to}
                linked
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function SummaryCards({
  performance,
  metric,
  onPick,
}: {
  performance: PortfolioPerformance;
  metric: PerformanceMetric;
  onPick: (metric: PerformanceMetric) => void;
}) {
  return (
    <section className="perf-summary" aria-label="Curva da mostrare">
      {METRICS.map((key) => {
        const summary = summarize(performance, key);
        const latest = summary.latest?.value ?? null;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={key === metric}
            className={key === metric ? 'perf-summary__card perf-summary__card--active' : 'perf-summary__card'}
            onClick={() => onPick(key)}
          >
            <span className="perf-summary__label">
              {METRIC_LABELS[key]}
              {key === 'combined' && <span className="perf-summary__sub"> · stato complessivo</span>}
            </span>
            <span className={`perf-summary__value numeric ${toneClass(latest)}`}>{signed(latest, performance.currency)}</span>
            <span className="perf-summary__hint">
              {summary.latest ? (
                <>
                  {statusOf(latest)}
                  {summary.latest.date !== performance.to && ` al ${formatDate(summary.latest.date)}`}
                  {summary.change !== null && (
                    <>
                      {' · nel periodo '}
                      <span className={toneClass(summary.change)}>{signed(summary.change, performance.currency)}</span>
                    </>
                  )}
                </>
              ) : (
                'nessun valore nel periodo'
              )}
            </span>
          </button>
        );
      })}
    </section>
  );
}

export function PortfolioPerformancePage() {
  const { id = '' } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const metricParam = params.get('metric');
  const metric: PerformanceMetric = isMetric(metricParam) ? metricParam : 'unrealized';
  const days = parseDays(params.get('days'));

  const [mode, setMode] = useState<PerformanceChartMode>('line');
  const [width, setWidth] = useState<CandleWidth>('day');
  const [showOthers, setShowOthers] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const portfolioQuery = usePortfolio(id);
  const performanceQuery = usePortfolioPerformance(id, days);
  const performance = performanceQuery.data;

  const candleWidth: CandleWidth = mode === 'candles' ? width : 'day';
  const buckets = useMemo(
    () => (performance ? buildBuckets(performance, metric, candleWidth) : []),
    [performance, metric, candleWidth],
  );
  // Memorizzate: un array nuovo a ogni render ricostruirebbe il grafico.
  const overlays = useMemo<PerformanceOverlay[]>(
    () =>
      performance && showOthers && mode === 'line'
        ? METRICS.filter((key) => key !== metric).map((key) => ({ metric: key, buckets: buildBuckets(performance, key, 'day') }))
        : [],
    [performance, showOthers, mode, metric],
  );

  const selectedBucket =
    buckets.find((bucket) => bucket.time === selected) ??
    buckets.find((bucket) => bucket.time === defaultSelection(buckets)) ??
    null;

  const update = (changes: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) next.set(key, value);
    setParams(next, { replace: true });
  };

  if (portfolioQuery.isError) {
    const error = portfolioQuery.error;
    return (
      <div className="portfolios">
        <Alert variant="error" title={error instanceof ApiError && error.status === 404 ? 'Portafoglio non trovato' : 'Errore di caricamento'}>
          {error instanceof ApiError ? error.message : 'Errore imprevisto.'}
        </Alert>
        <Link className="btn btn--secondary" to="/portfolios">
          Torna ai portafogli
        </Link>
      </div>
    );
  }

  const portfolio = portfolioQuery.data;
  const currency = performance?.currency ?? portfolio?.baseCurrency ?? 'EUR';
  const missing = metric === 'realized' ? 0 : (performance?.days.filter((day) => day[metric] === null).length ?? 0);
  const lastValued = performance ? [...performance.days].reverse().find((day) => day.unrealized !== null) : undefined;
  const unvalued = lastValued?.unvaluedHoldings ?? 0;

  return (
    <div className="portfolios perf">
      <header className="portfolios__header">
        <div>
          <nav className="portfolios__breadcrumb">
            <Link to="/portfolios">Portafogli</Link> <span aria-hidden="true">›</span>{' '}
            <Link to={`/portfolios/${id}`}>{portfolio?.name ?? '…'}</Link> <span aria-hidden="true">›</span>{' '}
            <span>Rendimento</span>
          </nav>
          <h1 className="portfolios__title">Grafico del rendimento</h1>
          <p className="portfolios__subtitle">
            Lo stato del portafoglio a fine di ogni giorno: sopra lo zero sei in guadagno, sotto sei in perdita. Non è il
            guadagno della singola giornata.
          </p>
        </div>
        <Link className="btn btn--secondary" to={`/portfolios/${id}`}>
          Torna al portafoglio
        </Link>
      </header>

      {performanceQuery.isError && (
        <Alert variant="error" title="Rendimento non disponibile">
          {performanceQuery.error instanceof ApiError ? performanceQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      )}

      {performanceQuery.isPending && <Spinner label="Ricostruzione del rendimento…" />}

      {performance && performance.days.length === 0 && (
        <EmptyState
          icon="📈"
          title="Ancora niente da disegnare"
          description="Il rendimento si ricostruisce dai movimenti e dalle fotografie giornaliere del portafoglio: importa o sincronizza il portafoglio per vederlo."
        />
      )}

      {performance && performance.days.length > 0 && (
        <>
          <SummaryCards
            performance={performance}
            metric={metric}
            onPick={(next) => {
              setSelected(null);
              update({ metric: next });
            }}
          />

          <div className="perf-toolbar">
            <Segmented
              label="Periodo"
              options={PERIODS.map((period) => ({ value: period.days, label: period.label, title: period.name }))}
              value={days}
              onChange={(next) => {
                setSelected(null);
                update({ days: next === null ? 'all' : String(next) });
              }}
            />
            <Segmented
              label="Tipo di grafico"
              options={[
                { value: 'line', label: 'Linea' },
                { value: 'candles', label: 'Candele' },
              ]}
              value={mode}
              onChange={(next) => {
                setSelected(null);
                setMode(next as PerformanceChartMode);
              }}
            />
            {mode === 'candles' ? (
              <Segmented
                label="Ampiezza della candela"
                options={[
                  { value: 'day', label: 'Giorno' },
                  { value: 'week', label: 'Settimana' },
                ]}
                value={width}
                onChange={(next) => {
                  setSelected(null);
                  setWidth(next as CandleWidth);
                }}
              />
            ) : (
              <Switch label="Mostra anche le altre due curve" checked={showOthers} onChange={setShowOthers} />
            )}
            <span className="perf-toolbar__range">
              {performance.from ? `${formatDate(performance.from)} – ${formatDate(performance.to)}` : ''}
              {performanceQuery.isFetching && ' · aggiornamento…'}
            </span>
          </div>

          <div className="perf-layout">
            <Card
              title={`${METRIC_LABELS[metric]} — stato a fine giornata`}
              subtitle={
                mode === 'line'
                  ? 'Ogni punto è lo stato cumulato a fine giornata. Il tratteggio grigio unisce due giorni con fotografia saltando quelli senza.'
                  : candleWidth === 'day'
                    ? 'Ogni candela apre alla chiusura del giorno prima e chiude a quella del giorno: verde se lo stato è migliorato. Con una lettura al giorno non ci sono ombre.'
                    : 'Ogni candela apre alla chiusura della settimana prima; le ombre sono il giorno migliore e il peggiore della settimana.'
              }
            >
              <div className="perf-legend" aria-hidden="true">
                {/* Nelle candele il colore e' la direzione del giorno; lo stato lo dice l'area tenue dietro. */}
                <span className="perf-legend__item perf-legend__item--gain">
                  {mode === 'line' ? 'in guadagno' : 'stato migliorato'}
                </span>
                <span className="perf-legend__item perf-legend__item--loss">
                  {mode === 'line' ? 'in perdita' : 'stato peggiorato'}
                </span>
                {overlays.map((overlay) => (
                  <span
                    key={overlay.metric}
                    className="perf-legend__item perf-legend__item--overlay"
                    style={{ ['--overlay-color' as string]: `var(${overlayToken(overlay.metric)})` }}
                  >
                    {METRIC_LABELS[overlay.metric].toLowerCase()}
                  </span>
                ))}
                <span className="perf-legend__marks">
                  <span className="perf-legend__mark perf-legend__mark--buy">acquisto</span>
                  <span className="perf-legend__mark perf-legend__mark--sell">vendita</span>
                  <span className="perf-legend__mark perf-legend__mark--income">dividendo</span>
                </span>
              </div>
              <PerformanceChart
                buckets={buckets}
                metric={metric}
                mode={mode}
                overlays={overlays}
                currency={currency}
                selected={selectedBucket?.time ?? null}
                onSelect={setSelected}
                renderTooltip={(bucket) => <Tooltip bucket={bucket} metric={metric} currency={currency} />}
              />
              {(missing > 0 || unvalued > 0) && (
                <p className="perf-note">
                  {missing > 0 &&
                    `${missing} ${missing === 1 ? 'giorno' : 'giorni'} su ${performance.days.length} senza fotografia del portafoglio: lì la non realizzata non è nota e non viene inventata. `}
                  {unvalued > 0 &&
                    `${unvalued} ${unvalued === 1 ? 'posizione è esclusa' : 'posizioni sono escluse'} dalla non realizzata perché senza valore di mercato o senza costo.`}
                </p>
              )}
            </Card>

            {selectedBucket && (
              <SelectedBucket bucket={selectedBucket} metric={metric} currency={currency} portfolioId={id} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
